import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { debounce } from "lodash";
import { SearchField } from "../../../../Components/formElements/SearchField";
import ItemService from "../../../../services/customer_appCommonService";
import toast from "react-hot-toast";

const MaterialSearchModal = React.forwardRef((props, ref) => {
  // ---------------- STATE ----------------

  //   const [getRentSaleData, setRentSaleData] = useState()
  //   const rentSaleDataRef = React.useRef(props.itemType);

  //   useEffect(() => {
  //   if (props.itemType) {
  //     setRentSaleData(props.itemType);
  //     rentSaleDataRef.current = props.itemType; // ← keep ref in sync
  //   }
  // }, [props.itemType]);
  const itemTypeRef = React.useRef(props.itemType);

  useEffect(() => {
    itemTypeRef.current = props.itemType;
  }, [props.itemType]);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  // eslint-disable-next-line
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTxt, setSearchTxt] = useState("");
  // eslint-disable-next-line
  const [isSearching, setIsSearching] = useState(false);

  // CART LOGIC
  const [selectedItems, setSelectedItems] = useState(new Map());
  const [isCardVisible, setCardVisible] = useState(true);

  const isMaterialMasterType = useMemo(
    () => props.itemType === "Nursing" || props.itemType === "Service",
    [props.itemType],
  );

  const getRowKey = useCallback(
    (row) => {
      if (isMaterialMasterType) {
        return row.materialCode; // Material Master → no barcode, use materialCode
      }
      return row.barcode; // Barcode inventory → use barcode
    },
    [isMaterialMasterType],
  );

  // ---------------- API ----------------

  const fetchMaterials = async (page, size, search) => {
    setLoading(true);
    if (search) setIsSearching(true);

    try {
      const isMateriaMasterTypeForNursing = props.itemType === "Nursing";
      const isMateriaMasterTypeForService = props.itemType === "Service";

      let payload;

      if (isMateriaMasterTypeForNursing) {
        payload = {
          queryName: "getMaterilMasterList",
          page,
          per_page: size,
          rowFilter: { $and: [{ materialGroup: "NURSING SERVICES" }] },
          timeZone: "Asia/Calcutta",
          ...(search.trim() && { searchTerm: search.trim() }),
        };
      } else if (isMateriaMasterTypeForService) {
        payload = {
          queryName: "getMaterilMasterList",
          page,
          per_page: size,
          rowFilter: { $and: [{ materialGroup: "SERVICES" }] },
          timeZone: "Asia/Calcutta",
          ...(search.trim() && { searchTerm: search.trim() }),
        };
      } else {
        // ── Barcode inventory for Rent / Sale ──────────────────
        let currentGodownCode = "";
        const currentType = itemTypeRef.current;
        if (currentType === "Sale") currentGodownCode = "SL";
        else if (currentType === "Rent") currentGodownCode = "RE";

        // ── Build rowFilter ────────────────────────────────────
        const andConditions = [];

        // If a hospital is selected, filter by hosptalCode
        if (props.selectedHospitalCode) {
          andConditions.push({
            "hospital.hosptalCode": props.selectedHospitalCode,
          });
        }

        payload = {
          queryName: "EquipmentNotAssignedBarcodeList",
          godownCode: currentGodownCode,
          page,
          per_page: size,
          searchTerm: search.trim(),
          timeZone: "Asia/Calcutta",
          ...(andConditions.length > 0 && {
            rowFilter: { $and: andConditions },
          }),
        };
      }

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200) {
        const formattedData = (response.data || []).map((item) => {
          let overriddenItem = { ...item, materialSelected: "ERFMS" };

          // ── Replace header-level prices with hospital-specific prices ──
          if (props.selectedHospitalCode && Array.isArray(item.hospital)) {
            const hospitalData = item.hospital.find(
              (h) => h.hosptalCode === props.selectedHospitalCode,
            );

            if (hospitalData) {
              overriddenItem = {
                ...overriddenItem,
                rentPerDayPrice: hospitalData.rentPerDayPrice,
                rent7DaysPrice: hospitalData.rent7DaysPrice,
                rent15DaysPrice: hospitalData.rent15DaysPrice,
                rent30DaysPrice: hospitalData.rent30DaysPrice,
                unitPrice: hospitalData.unitPrice,
                mrPrice: hospitalData.mrPrice,
                taxCode: hospitalData.taxCode,
                cog: hospitalData.cog,
                salePrice: hospitalData.salePrice,
              };
            }
          }

          return overriddenItem;
        });

        setData(formattedData);
        setTotalRows(response.total || 0);

        if (search && (!response.data || response.data.length === 0)) {
          toast(`No materials found matching "${search}"`);
        }
      } else {
        setData([]);
        setTotalRows(0);
        toast.error(response?.message || "Failed to fetch materials");
      }
    } catch (err) {
      console.error("Material fetch error:", err);
      setData([]);
      setTotalRows(0);
      toast.error("Error loading materials. Please try again.");
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

const fetchMaterialsRef = React.useRef(fetchMaterials);
useEffect(() => {
  fetchMaterialsRef.current = fetchMaterials;
});


  useEffect(() => {
    if (props.show) {
      setSelectedItems(new Map());
      setSearchTxt("");
      setCurrentPage(1);
      setIsSearching(false);
      fetchMaterials(1, perPage, "");
    } else {
      debouncedSearch.cancel();
    }
    // eslint-disable-next-line
  }, [props.show, props.itemType, props.selectedHospitalCode]); // ← added
  // useEffect(() => {
  //   if (props.show) {
  //     setSelectedItems(new Map());
  //     setSearchTxt("");
  //     setCurrentPage(1);
  //     setIsSearching(false);
  //     fetchMaterials(1, perPage, "");
  //   } else {
  //     debouncedSearch.cancel();
  //   }
  //   // eslint-disable-next-line
  // }, [props.show, props.itemType, props.selectedHospitalCode]); // Make sure itemType is included

  // ---------------- SEARCH ----------------
  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        setCurrentPage(1);
        // fetchMaterials(1, perPage, val);
        fetchMaterialsRef.current(1, perPage, val);
      }, 400),
    // eslint-disable-next-line
    [perPage],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTxt(val);

    if (!val.trim()) {
      debouncedSearch.cancel();
      setCurrentPage(1);
      fetchMaterials(1, perPage, "");
    } else {
      debouncedSearch(val);
    }
  };

  const handleClearSearch = () => {
    setSearchTxt("");
    setCurrentPage(1);
    debouncedSearch.cancel();
    fetchMaterials(1, perPage, "");
  };

  // ---------------- PAGINATION ----------------
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchMaterials(page, perPage, searchTxt);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchMaterials(page, newPerPage, searchTxt);
  };

  // ---------------- INIT ----------------
  // useEffect(() => {
  //   if (props.show) {
  //     setSelectedItems(new Map());
  //     setSearchTxt("");
  //     setCurrentPage(1);
  //     setIsSearching(false);
  //     fetchMaterials(1, perPage, "");
  //   } else {
  //     debouncedSearch.cancel();
  //   }
  //   // eslint-disable-next-line
  // }, [props.show, props.itemType]);

  // ---------------- CART / SELECTION ----------------
  // const handleSelectRow = useCallback((row) => {
  //   setSelectedItems((prev) => {
  //     const map = new Map(prev);
  //     if (map.has(row.barcode)) {
  //       map.delete(row.barcode);
  //     } else {
  //       map.set(row.barcode, row);
  //     }
  //     return map;
  //   });
  // }, []);

  // const handleSelectAll = useCallback(() => {
  //   setSelectedItems((prev) => {
  //     const map = new Map(prev);
  //     const allSelected = data.every((r) => map.has(r.barcode));

  //     data.forEach((r) => {
  //       if (allSelected) {
  //         map.delete(r.barcode);
  //       } else {
  //         map.set(r.barcode, r);
  //       }
  //     });
  //     return map;
  //   });
  // }, [data]);

  // const isRowSelected = useCallback(
  //   (barcode) => selectedItems.has(barcode),
  //   [selectedItems],
  // );

  // const areAllPageItemsSelected = useMemo(() => {
  //   return data.length > 0 && data.every((r) => selectedItems.has(r.barcode));
  // }, [data, selectedItems]);

  const handleSelectRow = useCallback(
    (row) => {
      const key = getRowKey(row);
      if (!key) return; // Guard: skip rows with no valid key
      setSelectedItems((prev) => {
        const map = new Map(prev);
        if (map.has(key)) {
          map.delete(key);
        } else {
          map.set(key, row);
        }
        return map;
      });
    },
    [getRowKey], // ← getRowKey in deps
  );

  const handleSelectAll = useCallback(() => {
    setSelectedItems((prev) => {
      const map = new Map(prev);
      const validRows = data.filter((r) => !!getRowKey(r)); // only rows with valid key
      const allSelected = validRows.every((r) => map.has(getRowKey(r)));
      validRows.forEach((r) => {
        const key = getRowKey(r);
        if (allSelected) {
          map.delete(key);
        } else {
          map.set(key, r);
        }
      });
      return map;
    });
  }, [data, getRowKey]);

  const handleClearAll = () => {
    setSelectedItems(new Map());
  };

  const isRowSelected = useCallback(
    (row) => {
      const key = getRowKey(row);
      if (!key) return false;
      return selectedItems.has(key);
    },
    [selectedItems, getRowKey], // ← getRowKey in deps
  );

  const areAllPageItemsSelected = useMemo(() => {
    if (data.length === 0) return false;
    const validRows = data.filter((r) => !!getRowKey(r));
    if (validRows.length === 0) return false;
    return validRows.every((r) => selectedItems.has(getRowKey(r)));
  }, [data, selectedItems, getRowKey]); // ← getRowKey in deps

  const handleSubmit = () => {
    if (selectedItems.size === 0) {
      toast("Please select at least one material");
      return;
    }
    props.onSelectedData(Array.from(selectedItems.values()));
    props.onHide();
  };

  // ---------------- TABLE COLUMNS ----------------
  const columns = useMemo(
    () => [
      {
        name: (
          <input
            type="checkbox"
            checked={areAllPageItemsSelected}
            onChange={handleSelectAll}
            disabled={loading || data.length === 0}
          />
        ),
        // cell: (row) => (
        //   <input
        //     type="checkbox"
        //     checked={isRowSelected(row.barcode)}
        //     onChange={() => handleSelectRow(row)}
        //   />
        // ),
        cell: (row) => (
          <input
            type="checkbox"
            checked={isRowSelected(row)} // ← pass full row now
            onChange={() => handleSelectRow(row)}
          />
        ),
        width: "60px",
        // center: true,
        style: { justifyContent: "center" },
      },
      // {
      //   name: "Barcode",
      //   selector: (row) => (
      //     <p className="m-0 fw-bold text-primary"> {row?.barcode || "--"}</p>
      //   ),
      //   width: "180px",
      //   sortable: true,
      // },
      {
        name: "Material Details",
        cell: (row) => (
          <div>
            <p className="m-0 fw-bold text-primary">
              {row.materialCode || "--"}
            </p>
            <p>{row.materialDescription || "--"} </p>
          </div>
        ),
        width: "200px",
        sortable: true,
      },
      {
        name: "HSN Code",
        cell: (row) => (
          <div>
            <p className="m-0 fw-bold text-primary">{row.hsnCode || "--"}</p>
          </div>
        ),
        width: "120px",
        sortable: true,
      },
      {
        name: "Targt Category",
        cell: (row) => (
          <div>
            <p className="m-0 fw-bold">{row.targetCategory || "--"}</p>
          </div>
        ),
        width: "150px",
        sortable: true,
      },
      {
        name: "Brand",
        cell: (row) => (
          <div>
            <p className="m-0 fw-bold text-primary">{row.brand || "--"}</p>
          </div>
        ),
        width: "120px",
        sortable: true,
      },
      {
        name: "Lock-In",
        cell: (row) => (
          <div>
            <p className="m-0 fw-bold">{row.lockInPeriod || "--"}</p>
          </div>
        ),
        width: "80px",
        sortable: true,
      },
      // {
      //   name: "Equipment Rent Details",
      //   cell: (row) => (
      //     <div>
      //       <p className="m-0 fw-bold text-primary">
      //       {row.equipmentRentNr || "--"}-{row.equipmentRentItemNr  || "--"}</p>
      //      {/* <p className="m-0 fw-bold text-primary">
      //       {row.equipmentRentItemNr  || "--"}(item Nr)</p>  */}

      //     </div>
      //   ),
      //   width: "200px",
      //   sortable: true,
      // },
      {
        name: "Vendor Price",
        selector: (row) => row?.unitPrice || 0,
        cell: (row) => `${Number(row?.unitPrice || 0).toFixed(2)}`,
        // right: true,
        style: { justifyContent: "flex-end" },
        width: "120px",
      },
      {
        name: "Sale Price",
        selector: (row) => row?.salePrice || 0,
        cell: (row) => `${Number(row?.salePrice || 0).toFixed(2)}`,
        // right: true,
        style: { justifyContent: "flex-end" },
        width: "120px",
      },
      {
        name: "Security Amt",
        selector: (row) => row?.securityAmt || 0,
        cell: (row) => `${Number(row?.securityAmt || 0).toFixed(2)}`,
        // right: true,
        style: { justifyContent: "flex-end" },
        width: "120px",
      },
      {
        name: "GST %",
        selector: (row) => row?.taxCode || 0,
        cell: (row) => `${Number(row?.taxCode || 0)}`,
        // right: true,
        center: true,
        // style: { justifyContent: 'flex-end' },
        width: "120px",
      },
      {
        name: "MRP",
        selector: (row) => row?.mrPrice || 0,
        cell: (row) => `${Number(row?.mrPrice || 0).toFixed(2)}`,
        // right: true,
        style: { justifyContent: "flex-end" },
        width: "120px",
      },
    ],
    [
      areAllPageItemsSelected,
      handleSelectAll,
      handleSelectRow,
      isRowSelected,
      loading,
      data.length,
    ],
  );

  // ---------------- UI ----------------
  return (
    <Modal
      {...props}
      dialogClassName="modal-90w"
      centered
      backdrop="static"
      keyboard={false}
      ref={ref}
      style={{ minHeight: "90vh" }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Material Selection</Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{ minHeight: "600px", maxHeight: "80vh", overflow: "auto" }}
      >
        {/* SEARCH BAR */}
        <Row className="mb-3">
          <Col
            xs={12}
            md={{ offset: 1, span: 4 }}
            lg={{ offset: 1, span: 4 }}
            className="commLeftRightPadding"
          >
            <div className="position-relative">
              <SearchField
                value={searchTxt}
                onChange={handleSearchChange}
                placeholder="Search materials..."
              />
              {searchTxt && (
                <i
                  className="bi bi-x-circle-fill position-absolute"
                  style={{
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#dc3545",
                    fontSize: "1.2rem",
                    zIndex: 5,
                  }}
                  onClick={handleClearSearch}
                  title="Clear search"
                ></i>
              )}
            </div>
            {/* Search hint */}
            {searchTxt && (
              <small className="text-muted d-block mt-1">
                Searching for "{searchTxt}"
              </small>
            )}
          </Col>
        </Row>

        {/* SELECTED ITEMS CART */}
        {selectedItems.size > 0 && (
          <Row className="mb-3">
            <Col xs={12}>
              <div className="border rounded p-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong className="">
                    <i className="bi me-2"></i>
                    Selected Items ({selectedItems.size})
                  </strong>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleClearAll}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Clear All
                  </button>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {/* {Array.from(selectedItems.values()).map((item) => (
                    <span
                      key={item.barcode}
                      className="badge bg-info d-flex align-items-center gap-2"
                      style={{ fontSize: "0.9rem", padding: "0.5rem 0.75rem" }}
                    >
                      <span>{item.materialCode}</span>
                      <i
                        className="bi bi-x-circle-fill"
                        style={{ cursor: "pointer", fontSize: "1rem" }}
                        onClick={() => handleSelectRow(item)}
                        title="Remove"
                      ></i>
                    </span>
                  ))} */}
                  {Array.from(selectedItems.values()).map((item) => {
                    const key = isMaterialMasterType
                      ? item.materialCode
                      : item.barcode;
                    return (
                      <span
                        key={key} // ← correct key for both types
                        className="badge bg-info d-flex align-items-center gap-2"
                        style={{
                          fontSize: "0.9rem",
                          padding: "0.5rem 0.75rem",
                        }}
                      >
                        <span>{item.materialCode}</span>
                        <i
                          className="bi bi-x-circle-fill"
                          style={{ cursor: "pointer", fontSize: "1rem" }}
                          onClick={() => handleSelectRow(item)}
                          title="Remove"
                        ></i>
                      </span>
                    );
                  })}
                </div>
              </div>
            </Col>
          </Row>
        )}

        {/* MATERIAL TABLE */}
        <Row>
          <Col xs={12}>
            <div className="accordion">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button align-items-center">
                    <Col xs={1} className="text-center">
                      <i
                        className={`bi ${
                          isCardVisible ? "bi-chevron-right" : "bi-chevron-down"
                        }`}
                        onClick={() => setCardVisible(!isCardVisible)}
                        style={{ cursor: "pointer", fontSize: "1.2rem" }}
                      ></i>
                    </Col>
                    <Col xs={11}>
                      <span className="headerTitle">
                        Material/Product List
                        {!loading && totalRows > 0 && (
                          <span className="text-muted ms-2 fw-normal">
                            ({totalRows} items)
                          </span>
                        )}
                      </span>
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    {/*  Table container */}
                    <div
                      className="position-relative"
                      style={{
                        height: "450px",
                        overflow: "hidden",
                      }}
                    >
                      {/* Loading Overlay */}
                      {loading && (
                        <div
                          className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                          style={{
                            zIndex: 10,
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(2px)",
                          }}
                        >
                          <div
                            className="spinner-border text-primary mb-3"
                            role="status"
                            style={{ width: "3rem", height: "3rem" }}
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h6 className="text-primary mb-2">
                            {searchTxt
                              ? `Searching for "${searchTxt}"...`
                              : "Loading materials..."}
                          </h6>
                          <p className="text-muted small mb-0">Please wait</p>
                        </div>
                      )}

                      {/* DataTable */}
                      <DataTable
                        columns={columns}
                        data={data}
                        progressPending={false}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        highlightOnHover
                        pointerOnHover
                        fixedHeader
                        fixedHeaderScrollHeight="380px"
                        noDataComponent={
                          <div className="text-center py-5">
                            <i
                              className="bi bi-inbox text-muted"
                              style={{ fontSize: "3rem" }}
                            ></i>
                            <p className="text-muted mt-3 mb-2">
                              {searchTxt
                                ? `No materials found matching "${searchTxt}"`
                                : "No materials available"}
                            </p>
                            {searchTxt && (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={handleClearSearch}
                              >
                                <i className="bi bi-x-circle me-1"></i>
                                Clear Search
                              </button>
                            )}
                          </div>
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>

      {/* FOOTER */}
      <Modal.Footer className="bg-light">
        <Row className="w-100 align-items-center">
          <Col xs={6} className="text-start">
            {selectedItems.size > 0 && (
              <span className="text-muted fw-medium">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                {selectedItems.size} item(s) selected
              </span>
            )}
          </Col>
          <Col xs={6} className="text-end">
            <button
              type="button"
              className="btn btn-danger mt-4 me-4  justify-center rounded-md"
              onClick={props.onHide}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary mt-4  justify-center rounded-md"
              disabled={selectedItems.size === 0}
              onClick={handleSubmit}
            >
              Select
            </button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
});

MaterialSearchModal.displayName = "MaterialSearchModal";
export default MaterialSearchModal;
